/**
 * T3C 初始化 Hook
 * @description 处理 T3C SDK 初始化、通话时长获取、400热线配置、监听器注册等逻辑
 */

import { useEffect, useCallback } from 'react'
import { useStore } from 'zustand'
import { t3cStore } from '@store/t3c-store'
import { sessionStore, shouldInitT3C } from '@store/session-store'
import { useShallow } from 'zustand/shallow'
import { moduleId } from '@configs'
import { registerT3cBaseListeners } from '@store/t3c-listeners'

interface T3cInitOptions {
  /** 是否刷新 SDK（默认 false） */
  refresh?: boolean
}

/**
 * T3C 初始化 Hook
 * 当用户信息加载完成后，自动初始化 T3C SDK 并注册事件监听器
 * @param options.refresh 是否强制刷新 SDK，默认 false
 */
export const useT3cInitialization = (options: T3cInitOptions = {}) => {
  const { refresh = false } = options

  // 获取用户信息
  const { userInfo } = sessionStore(
    useShallow(s => ({
      userInfo: s.userInfo,
    }))
  )

  // 获取是否应该初始化 T3C 的派生状态（使用 useStore 订阅变化）
  const canInitT3C = useStore(shouldInitT3C)

  // 获取 T3C 相关状态和方法
  const {
    initT3cSdk,
    getTodayCallDuration,
    getHotline400ConfigOnce,
  } = t3cStore(
    useShallow(s => ({
      initT3cSdk: s.initT3cSdk,
      getTodayCallDuration: s.getTodayCallDuration,
      getHotline400ConfigOnce: s.getHotline400ConfigOnce,
    }))
  )

  // SDK Ready 回调：只注册基础监听器
  const onSdkReady = useCallback(() => {
    registerT3cBaseListeners()
  }, [])

  // T3C Ready 后注册扩展监听器


  // 初始化 T3C
  useEffect(() => {
    const initializeT3c = async () => {
      // 使用派生状态判断是否应该初始化 T3C SDK
      if (!canInitT3C) {
        if (userInfo.useT3C && !userInfo.email?.trim()) {
          console.log(`用户 ${userInfo.salesName} (ID: ${userInfo.id}) 未绑定邮箱，不初始化 T3C SDK`)
        }
        return
      }

      try {
        // 获取今日通话时长
        await getTodayCallDuration()

        // 初始化 T3C SDK，传入回调注册监听器
        await initT3cSdk({ refresh }, onSdkReady, userInfo)

        // 如果是清洗-商机管理中台，则加载400热线相关配置
        if (userInfo.belongModule === moduleId.MID_LONG_TAIL_CLEAN_ID) {
          await getHotline400ConfigOnce()
        }
      } catch (error) {
        console.error('T3C 初始化失败:', error)
      }
    }

    initializeT3c()
  }, [canInitT3C, userInfo])
}
